import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { toast } from "sonner";
import { useAuth } from "../contexts/AuthContext";
import { DeleteConfirmDialog } from "../components/pipeline/DeleteConfirmDialog";
import { RuleModal } from "../components/catalog/RuleModal";
import { ServicesTab } from "../components/catalog/ServicesTab";
import { CategoriesTab } from "../components/catalog/CategoriesTab";
import { ConvergentTab } from "../components/catalog/ConvergentTab";
import { RulesTab } from "../components/catalog/RulesTab";
import { ServiceModal } from "../components/catalog/ServiceModal";
import { CategoryModal } from "../components/catalog/CategoryModal";
import { ConvergentPackageModal } from "../components/catalog/ConvergentPackageModal";
import {
  Plus,
  Package,
  Tag,
  Scale,
  Layers,
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Single source of truth for user-visible labels of Package Builder
// category-assignment values. Values themselves (CONVERGENTE, MOVILES_PAQ, …)
// remain unchanged — only the human-readable label is centralized here.
const PACKAGE_BUILDER_BLOCK_LABELS = {
  CONVERGENTE:         "Paquete convergente",
  MOVILES_PAQ:         "M\u00f3viles adicionales",
  BONO_MOVIL:          "Bonos m\u00f3viles",
  SOLO_FIBRA:          "Fibra",
  SOLO_MOVIL:          "M\u00f3vil",
  IP_ADICIONAL:        "IP fija para fibra",
  TV:                  "TV",
  DESCUENTO_TV:        "Descuentos TV",
  CENTRALITA:          "Centralita",
  TERMINAL:            "Terminales",
  NUMERACION:          "Numeraci\u00f3n / DDI",
  BONO_CENTRALITA:     "Bonos centralita",
  DESCUENTO_CENTRALITA:"Descuentos centralita",
  WIFI:                "WiFi Pyme",
  DESCUENTO_WIFI:      "Descuentos WiFi",
  OTRO:                "Otros",
};

// Selector options (checkbox list) — values MUST keep the canonical enum
// identifiers; only `label` is the displayed string from the map above.
const BLOQUE_OPTIONS = [
  { value: "",                     label: "No aparece en Armar Paquete" },
  ...Object.entries(PACKAGE_BUILDER_BLOCK_LABELS)
    .filter(([v]) => v !== "OTRO")
    .map(([value, label]) => ({ value, label })),
  { value: "OTRO", label: `${PACKAGE_BUILDER_BLOCK_LABELS.OTRO} (no aparece en Armar Paquete)` },
];

// Legacy export name kept for existing import sites; re-uses the same map.
const BLOQUE_LABELS = PACKAGE_BUILDER_BLOCK_LABELS;

// Resolve block assignments for a category, with backward compatibility for
// the legacy single-value `bloque_package_builder` field.
const getCategoryBlocks = (category) => {
  if (!category) return [];
  if (Array.isArray(category.bloques_package_builder) && category.bloques_package_builder.length) {
    return category.bloques_package_builder;
  }
  return category.bloque_package_builder ? [category.bloque_package_builder] : [];
};

// Helper: convert numeric value to string for price text inputs
// Format currency for display
const formatCurrency = (value) => {
  if (value === null || value === undefined) return "—";
  return new Intl.NumberFormat("es-ES", {
    style: "currency",
    currency: "EUR",
  }).format(value);
};

// ServiceForm and its price-string helper now live in components/catalog/ServiceModal.jsx



const CatalogPage = () => {
  const { canDelete, canEdit } = useAuth();
  const [activeTab, setActiveTab] = useState("services");
  const [services, setServices] = useState([]);
  const [providers, setProviders] = useState([]);
  const [categories, setCategories] = useState([]);
  const [packages, setPackages] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [newService, setNewService] = useState({
    nombre: "",
    categoria: "",
    descripcion: "",
    proveedor_id: "",
    precio_base: null,
    permanencia_meses: 12,
    penalizacion_cancelacion: 0,
    is_active: true,
    precio_paquete: null,
    disponible_standalone: true,
    unidades_por_seleccion: 1,
  });
  
  // Category management
  const [categoryModalOpen, setCategoryModalOpen] = useState(false);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ name: "", is_active: true, bloques_package_builder: [] });

  // Package management
  const [packageModalOpen, setPackageModalOpen] = useState(false);
  const [editingPackage, setEditingPackage] = useState(null);
  const [newPackage, setNewPackage] = useState({
    nombre: "",
    descripcion: "",
    tipo: "CONVERGENTE",
    componentes: [],
    is_active: true,
  });

  // Rules management
  const [ruleModalOpen, setRuleModalOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [ruleTypeFilter, setRuleTypeFilter] = useState("all");
  const [newRule, setNewRule] = useState({
    nombre: "",
    descripcion: "",
    tipo: "EXCLUSIVIDAD",
    aplica_a_paquete_id: null,
    condicion_producto_id: null,
    condicion_producto_nombre: null,
    producto_requerido_id: null,
    producto_requerido_nombre: null,
    producto_objetivo_id: null,
    producto_objetivo_nombre: null,
    categoria_condicion: null,
    categoria_requerida: null,
    mensaje_error: "",
    mensaje_sugerencia: "",
    is_active: true,
  });

  // Convergent packages management
  const [convergentPackages, setConvergentPackages] = useState([]);
  const [convergentModalOpen, setConvergentModalOpen] = useState(false);
  const [editingConvergent, setEditingConvergent] = useState(null);
  const [convergentServiceIds, setConvergentServiceIds] = useState([]);
  const [convergentForm, setConvergentForm] = useState({ nombre: "", descripcion: "" });

  // Check if user can manage Package Builder rules (mapped to catalog.edit)
  // RBAC v2: rules management is data-driven — mapped to catalog.edit
  // (SUPERADMIN + GESTION grant it by default, but now fully matrix-configurable).
  const canManageRules = canEdit("catalog");

  // Delete confirmation state
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, title: "", message: "", onConfirm: null });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [servicesRes, providersRes, categoriesRes, packagesRes, rulesRes, convergentRes] = await Promise.all([
        axios.get(`${API_URL}/api/catalog-services`),
        axios.get(`${API_URL}/api/providers?is_active=true`),
        axios.get(`${API_URL}/api/service-categories`),
        axios.get(`${API_URL}/api/catalog-packages`),
        axios.get(`${API_URL}/api/business-rules`),
        axios.get(`${API_URL}/api/convergent-packages`),
      ]);
      setServices(servicesRes.data);
      setProviders(providersRes.data);
      setCategories(categoriesRes.data);
      setPackages(packagesRes.data);
      setRules(rulesRes.data);
      setConvergentPackages(convergentRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Error al cargar catálogo");
    } finally {
      setLoading(false);
    }
  };

  const resetNewService = () => {
    setNewService({
      nombre: "",
      categoria: "",
      descripcion: "",
      proveedor_id: "",
      precio_base: null,
      permanencia_meses: 12,
      penalizacion_cancelacion: 0,
      is_active: true,
      precio_paquete: null,
      disponible_standalone: true,
      unidades_por_seleccion: 1,
    });
  };

  const handleCreateService = async () => {
    if (!newService.nombre || !newService.categoria) {
      toast.error("Nombre y categoría son obligatorios");
      return;
    }

    // Validate that at least one price is set
    if (newService.precio_base === null && newService.precio_paquete === null) {
      toast.error("Debe indicar al menos un precio (standalone o paquete)");
      return;
    }

    try {
      const serviceData = {
        ...newService,
        precio_base: newService.precio_base,
        precio_paquete: newService.precio_paquete,
        penalizacion_cancelacion: parseFloat(newService.penalizacion_cancelacion) || 0,
      };
      await axios.post(`${API_URL}/api/catalog-services`, serviceData);
      toast.success("Servicio creado");
      setCreateOpen(false);
      resetNewService();
      fetchData();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al crear servicio");
    }
  };

  const handleUpdateService = async () => {
    if (!editingService) return;

    try {
      const serviceData = {
        ...editingService,
        precio_base: editingService.precio_base,
        precio_paquete: editingService.precio_paquete,
        penalizacion_cancelacion: parseFloat(editingService.penalizacion_cancelacion) || 0,
      };
      await axios.put(`${API_URL}/api/catalog-services/${editingService.id}`, serviceData);
      toast.success("Servicio actualizado");
      setEditingService(null);
      fetchData();
    } catch (error) {
      console.error("Error:", error);
      toast.error("Error al actualizar servicio");
    }
  };

  const handleDeleteService = async (id) => {
    setDeleteConfirm({
      open: true,
      title: "Eliminar servicio",
      message: "¿Eliminar este servicio del catálogo? Esta acción no se puede deshacer.",
      onConfirm: async () => {
        setDeleteConfirm(prev => ({ ...prev, open: false }));
        try {
          await axios.delete(`${API_URL}/api/catalog-services/${id}`);
          toast.success("Servicio eliminado");
          fetchData();
        } catch (error) {
          toast.error("Error al eliminar servicio");
        }
      },
    });
  };

  // Category handlers
  const handleCreateCategory = async () => {
    if (!newCategory.name.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }

    try {
      await axios.post(`${API_URL}/api/service-categories`, newCategory);
      toast.success("Categoría creada");
      setCategoryModalOpen(false);
      setNewCategory({ name: "", is_active: true, bloques_package_builder: [] });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear categoría");
    }
  };

  const handleUpdateCategory = async () => {
    if (!editingCategory) return;

    try {
      await axios.put(`${API_URL}/api/service-categories/${editingCategory.id}`, editingCategory);
      toast.success("Categoría actualizada");
      setEditingCategory(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar categoría");
    }
  };

  const handleDeleteCategory = async (id) => {
    setDeleteConfirm({
      open: true,
      title: "Eliminar categoría",
      message: "¿Eliminar esta categoría? Los servicios asociados quedarán sin categoría.",
      onConfirm: async () => {
        setDeleteConfirm(prev => ({ ...prev, open: false }));
        try {
          await axios.delete(`${API_URL}/api/service-categories/${id}`);
          toast.success("Categoría eliminada");
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Error al eliminar categoría");
        }
      },
    });
  };

  // Package handlers
  const resetNewPackage = () => {
    setNewPackage({
      nombre: "",
      descripcion: "",
      tipo: "CONVERGENTE",
      componentes: [],
      is_active: true,
    });
  };

  const handleCreatePackage = async () => {
    if (!newPackage.nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }

    try {
      await axios.post(`${API_URL}/api/catalog-packages`, newPackage);
      toast.success("Paquete creado");
      setPackageModalOpen(false);
      resetNewPackage();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear paquete");
    }
  };

  const handleUpdatePackage = async () => {
    if (!editingPackage) return;

    try {
      await axios.put(`${API_URL}/api/catalog-packages/${editingPackage.id}`, editingPackage);
      toast.success("Paquete actualizado");
      setEditingPackage(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar paquete");
    }
  };

  const handleDeletePackage = async (id) => {
    setDeleteConfirm({
      open: true,
      title: "Eliminar paquete",
      message: "¿Eliminar este paquete? Esta acción no se puede deshacer.",
      onConfirm: async () => {
        setDeleteConfirm(prev => ({ ...prev, open: false }));
        try {
          await axios.delete(`${API_URL}/api/catalog-packages/${id}`);
          toast.success("Paquete eliminado");
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Error al eliminar paquete");
        }
      },
    });
  };

  const handleTogglePackage = async (pkg) => {
    try {
      await axios.put(`${API_URL}/api/catalog-packages/${pkg.id}`, { is_active: !pkg.is_active });
      toast.success(pkg.is_active ? "Paquete desactivado" : "Paquete activado");
      fetchData();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  // Convergent package handlers
  const openConvergentCreate = () => {
    setConvergentForm({ nombre: "", descripcion: "" });
    setConvergentServiceIds([]);
    setEditingConvergent(null);
    setConvergentModalOpen(true);
  };

  const openConvergentEdit = (pkg) => {
    setConvergentForm({ nombre: pkg.nombre, descripcion: pkg.descripcion || "" });
    // Extract unique catalog_service_ids (deduplicate for multi-unit services)
    const seen = new Set();
    const ids = [];
    pkg.servicios.forEach(s => {
      if (!seen.has(s.catalog_service_id)) {
        seen.add(s.catalog_service_id);
        ids.push(s.catalog_service_id);
      }
    });
    setConvergentServiceIds(ids);
    setEditingConvergent(pkg);
    setConvergentModalOpen(true);
  };

  const handleSaveConvergent = async () => {
    if (!convergentForm.nombre.trim()) {
      toast.error("El nombre es obligatorio");
      return;
    }
    if (convergentServiceIds.length === 0) {
      toast.error("Debes seleccionar al menos un servicio");
      return;
    }
    const payload = {
      nombre: convergentForm.nombre,
      descripcion: convergentForm.descripcion || null,
      servicios: convergentServiceIds.map(id => ({
        catalog_service_id: id,
        nombre: "",
        precio: 0,
        categoria: "",
        linea_numero: null,
      })),
      is_active: editingConvergent ? editingConvergent.is_active : true,
    };
    try {
      if (editingConvergent) {
        await axios.put(`${API_URL}/api/convergent-packages/${editingConvergent.id}`, payload);
        toast.success("Paquete convergente actualizado");
      } else {
        await axios.post(`${API_URL}/api/convergent-packages`, payload);
        toast.success("Paquete convergente creado");
      }
      setConvergentModalOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al guardar paquete convergente");
    }
  };

  const handleDeleteConvergent = async (id) => {
    setDeleteConfirm({
      open: true,
      title: "Eliminar paquete convergente",
      message: "¿Eliminar este paquete convergente? Esta acción no se puede deshacer.",
      onConfirm: async () => {
        setDeleteConfirm(prev => ({ ...prev, open: false }));
        try {
          await axios.delete(`${API_URL}/api/convergent-packages/${id}`);
          toast.success("Paquete convergente eliminado");
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Error al eliminar");
        }
      },
    });
  };

  const handleToggleConvergent = async (pkg) => {
    try {
      await axios.patch(`${API_URL}/api/convergent-packages/${pkg.id}/toggle`);
      toast.success(pkg.is_active ? "Paquete desactivado" : "Paquete activado");
      fetchData();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  // All active services for convergent packages (grouped by category in the UI)
  const convergentEligibleServices = services.filter(s => s.is_active);


  // Rule handlers
  const resetNewRule = () => {
    setNewRule({
      nombre: "",
      descripcion: "",
      tipo: "EXCLUSIVIDAD",
      aplica_a_paquete_id: null,
      condicion_producto_id: null,
      condicion_producto_nombre: null,
      producto_requerido_id: null,
      producto_requerido_nombre: null,
      producto_objetivo_id: null,
      producto_objetivo_nombre: null,
      categoria_condicion: null,
      categoria_requerida: null,
      mensaje_error: "",
      mensaje_sugerencia: "",
      is_active: true,
    });
  };

  const handleCreateRule = async () => {
    if (!newRule.nombre.trim() || !newRule.mensaje_error.trim()) {
      toast.error("Nombre y mensaje de error son obligatorios");
      return;
    }

    try {
      await axios.post(`${API_URL}/api/business-rules`, newRule);
      toast.success("Regla creada");
      setRuleModalOpen(false);
      resetNewRule();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear regla");
    }
  };

  const handleUpdateRule = async () => {
    if (!editingRule) return;

    try {
      await axios.put(`${API_URL}/api/business-rules/${editingRule.id}`, editingRule);
      toast.success("Regla actualizada");
      setEditingRule(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al actualizar regla");
    }
  };

  const handleDeleteRule = async (id) => {
    setDeleteConfirm({
      open: true,
      title: "Eliminar regla",
      message: "¿Eliminar esta regla de negocio? Esta acción no se puede deshacer.",
      onConfirm: async () => {
        setDeleteConfirm(prev => ({ ...prev, open: false }));
        try {
          await axios.delete(`${API_URL}/api/business-rules/${id}`);
          toast.success("Regla eliminada");
          fetchData();
        } catch (error) {
          toast.error(error.response?.data?.detail || "Error al eliminar regla");
        }
      },
    });
  };

  const handleToggleRule = async (rule) => {
    try {
      await axios.patch(`${API_URL}/api/business-rules/${rule.id}/toggle`);
      toast.success(rule.is_active ? "Regla desactivada" : "Regla activada");
      fetchData();
    } catch (error) {
      toast.error("Error al cambiar estado");
    }
  };

  // Handler for updating fields in newService
  const handleNewServiceFieldChange = useCallback((field, value) => {
    setNewService(prev => ({ ...prev, [field]: value }));
  }, []);

  // Handler for updating fields in editingService
  const handleEditingServiceFieldChange = useCallback((field, value) => {
    setEditingService(prev => prev ? { ...prev, [field]: value } : null);
  }, []);

  // Add component to package
  const addComponentToPackage = (pkg, setPkg) => {
    const newComponent = {
      id: `temp-${Date.now()}`,
      etiqueta: "",
      categoria: categories[0]?.name || "",
      es_obligatorio: false,
      min_unidades: 0,
      max_unidades: 1,
      productos_permitidos: [],
      orden: (pkg.componentes?.length || 0) + 1,
    };
    setPkg({ ...pkg, componentes: [...(pkg.componentes || []), newComponent] });
  };

  // Remove component from package
  const removeComponentFromPackage = (pkg, setPkg, index) => {
    const newComponents = [...pkg.componentes];
    newComponents.splice(index, 1);
    setPkg({ ...pkg, componentes: newComponents });
  };

  // Update component in package
  const updateComponentInPackage = (pkg, setPkg, index, field, value) => {
    const newComponents = [...pkg.componentes];
    newComponents[index] = { ...newComponents[index], [field]: value };
    setPkg({ ...pkg, componentes: newComponents });
  };

  // Move component up/down
  const moveComponent = (pkg, setPkg, index, direction) => {
    const newComponents = [...pkg.componentes];
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= newComponents.length) return;
    [newComponents[index], newComponents[newIndex]] = [newComponents[newIndex], newComponents[index]];
    // Update orden
    newComponents.forEach((comp, i) => comp.orden = i + 1);
    setPkg({ ...pkg, componentes: newComponents });
  };

  // Group services by category
  const groupedServices = services.reduce((acc, service) => {
    const cat = service.categoria || "Sin categoría";
    if (!acc[cat]) {
      acc[cat] = [];
    }
    acc[cat].push(service);
    return acc;
  }, {});

  // Filter rules by type
  const filteredRules = ruleTypeFilter === "all" 
    ? rules 
    : rules.filter(r => r.tipo === ruleTypeFilter);

  // Get rule type badge color
  const getRuleTypeBadgeClass = (tipo) => {
    switch (tipo) {
      case "EXCLUSIVIDAD": return "bg-orange-500/20 text-orange-400";
      case "REQUIERE_CATEGORIA": return "bg-blue-500/20 text-blue-400";
      case "DESCUENTO_CONDICIONAL": return "bg-purple-500/20 text-purple-400";
      case "BLOQUEO_STANDALONE": return "bg-red-500/20 text-red-400";
      default: return "bg-slate-500/20 text-slate-400";
    }
  };

  // Get discount products (precio_paquete < 0)
  const discountProducts = services.filter(s => s.precio_paquete !== null && s.precio_paquete < 0);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-[#42E695]"></div>
      </div>
    );
  }

  return (
    <>
    <div className="space-y-6" data-testid="catalog-page">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl md:text-3xl font-heading font-semibold text-white flex items-center gap-3">
            <Package className="h-8 w-8 text-[#03BEE4]" />
            Catálogo de Servicios
          </h1>
          <p className="text-slate-400 mt-1">Gestión del catálogo maestro de productos y servicios</p>
        </div>
        <Button
          onClick={() => setCreateOpen(true)}
          className="bg-[#42E695] text-[#04042C] hover:bg-[#42E695]/90"
          data-testid="create-service-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nuevo Servicio
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="bg-[#0A0A35]">
          <TabsTrigger value="services" className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]">
            <Package className="h-4 w-4 mr-2" />
            Servicios
          </TabsTrigger>
          <TabsTrigger value="categories" className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]">
            <Tag className="h-4 w-4 mr-2" />
            Categorías
          </TabsTrigger>
          {/* Paquetes Convergentes tab */}
          <TabsTrigger value="convergent" className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]">
            <Layers className="h-4 w-4 mr-2" />
            Paquetes Convergentes
          </TabsTrigger>
          {canManageRules && (
            <TabsTrigger value="rules" className="data-[state=active]:bg-[#42E695] data-[state=active]:text-[#04042C]">
              <Scale className="h-4 w-4 mr-2" />
              Reglas de Negocio
            </TabsTrigger>
          )}
        </TabsList>

        {/* Services Tab */}
        <ServicesTab
          services={services}
          categories={categories}
          providers={providers}
          groupedServices={groupedServices}
          formatCurrency={formatCurrency}
          setCreateOpen={setCreateOpen}
          setEditingService={setEditingService}
          canDelete={canDelete}
          handleDeleteService={handleDeleteService}
        />

        {/* Categories Tab */}
        <CategoriesTab
          categories={categories}
          services={services}
          setCategoryModalOpen={setCategoryModalOpen}
          getCategoryBlocks={getCategoryBlocks}
          BLOQUE_LABELS={BLOQUE_LABELS}
          setEditingCategory={setEditingCategory}
          canDelete={canDelete}
          handleDeleteCategory={handleDeleteCategory}
        />

        {/* Packages tab removed */}

        {/* Convergent Packages Tab */}
        <ConvergentTab
          convergentPackages={convergentPackages}
          openConvergentCreate={openConvergentCreate}
          openConvergentEdit={openConvergentEdit}
          formatCurrency={formatCurrency}
          handleToggleConvergent={handleToggleConvergent}
          canDelete={canDelete}
          handleDeleteConvergent={handleDeleteConvergent}
        />

        {/* Rules Tab */}
        {canManageRules && (
          <RulesTab
            ruleTypeFilter={ruleTypeFilter}
            setRuleTypeFilter={setRuleTypeFilter}
            filteredRules={filteredRules}
            setRuleModalOpen={setRuleModalOpen}
            setEditingRule={setEditingRule}
            getRuleTypeBadgeClass={getRuleTypeBadgeClass}
            handleToggleRule={handleToggleRule}
            canDelete={canDelete}
            handleDeleteRule={handleDeleteRule}
          />
        )}
      </Tabs>

      {/* Create/Edit Service Modals */}
      <ServiceModal
        createOpen={createOpen}
        setCreateOpen={setCreateOpen}
        newService={newService}
        handleNewServiceFieldChange={handleNewServiceFieldChange}
        handleCreateService={handleCreateService}
        editingService={editingService}
        setEditingService={setEditingService}
        handleEditingServiceFieldChange={handleEditingServiceFieldChange}
        handleUpdateService={handleUpdateService}
        providers={providers}
        categories={categories}
      />

      {/* Create Category Modal */}
      <CategoryModal
        categoryModalOpen={categoryModalOpen}
        setCategoryModalOpen={setCategoryModalOpen}
        newCategory={newCategory}
        setNewCategory={setNewCategory}
        editingCategory={editingCategory}
        setEditingCategory={setEditingCategory}
        BLOQUE_OPTIONS={BLOQUE_OPTIONS}
        handleCreateCategory={handleCreateCategory}
        handleUpdateCategory={handleUpdateCategory}
      />

      {/* Package modal removed */}

      {/* Convergent Package Modal */}
      <ConvergentPackageModal
        convergentModalOpen={convergentModalOpen}
        setConvergentModalOpen={setConvergentModalOpen}
        editingConvergent={editingConvergent}
        convergentForm={convergentForm}
        setConvergentForm={setConvergentForm}
        convergentServiceIds={convergentServiceIds}
        setConvergentServiceIds={setConvergentServiceIds}
        services={services}
        convergentEligibleServices={convergentEligibleServices}
        formatCurrency={formatCurrency}
        handleSaveConvergent={handleSaveConvergent}
      />

      {/* Create/Edit Rule Modal */}
      <RuleModal
        ruleModalOpen={ruleModalOpen}
        setRuleModalOpen={setRuleModalOpen}
        editingRule={editingRule}
        setEditingRule={setEditingRule}
        newRule={newRule}
        setNewRule={setNewRule}
        services={services}
        categories={categories}
        discountProducts={discountProducts}
        formatCurrency={formatCurrency}
        resetNewRule={resetNewRule}
        handleCreateRule={handleCreateRule}
        handleUpdateRule={handleUpdateRule}
      />
    </div>

    <DeleteConfirmDialog
      open={deleteConfirm.open}
      onOpenChange={(open) => !open && setDeleteConfirm(prev => ({ ...prev, open: false }))}
      onConfirm={deleteConfirm.onConfirm}
      onCancel={() => setDeleteConfirm(prev => ({ ...prev, open: false }))}
      title={deleteConfirm.title}
      message={deleteConfirm.message}
    />
    </>
  );
};

export default CatalogPage;
